import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { copyComputedStyles, ensurePoppinsLoaded } from './helper';

/**
 * Renders a report DOM element to PDF and triggers download.
 * Shared by app GenerateReport and admin class report wrapper.
 * Loads Poppins before capture and uses a clone with inlined computed styles so PDF does not depend on external CSS (avoids 404/stale font after deploy).
 */
export async function generateReportPdfFromElement(
  element: HTMLElement,
  filename: string
): Promise<void> {
  element.style.display = 'flex';
  element.style.visibility = 'visible';
  element.dataset.reportrendering = '1';
  element.style.setProperty('background-color', 'rgba(90, 90, 90, 0.08)', 'important');

  const originalZooms: { element: Element; zoom: string }[] = [];
  let currentElement: Element | null = element.parentElement;

  while (currentElement) {
    const computedStyle = window.getComputedStyle(currentElement);
    const zoom = (computedStyle as any).zoom;
    if (zoom !== '1') {
      originalZooms.push({ element: currentElement, zoom });
      (currentElement as HTMLElement).style.setProperty('zoom', '1');
    }
    currentElement = currentElement.parentElement;
  }

  await ensurePoppinsLoaded();
  await new Promise<void>(resolve => setTimeout(resolve, 2000));

  element.style.setProperty('min-height', '0', 'important');

  const clone = element.cloneNode(true) as HTMLElement;
  copyComputedStyles(element, clone);
  clone.style.position = 'absolute';
  clone.style.left = '-9999px';
  clone.style.top = '0';
  clone.style.width = `${element.offsetWidth}px`;
  clone.style.height = `${element.offsetHeight}px`;
  document.body.appendChild(clone);

  try {
    const canvas = await html2canvas(clone, { scale: 5 });
    document.body.removeChild(clone);

    element.style.visibility = 'hidden';
    element.style.display = 'none';
    element.dataset.reportrendering = '0';

    originalZooms.forEach(({ element: el, zoom }) => {
      (el as HTMLElement).style.setProperty('zoom', zoom);
    });

    const imgData = canvas.toDataURL('image/jpeg');
    const imgHeight = canvas.height;
    const imgWidth = canvas.width;
    const hRatio = imgHeight / imgWidth;

    const pdf = new jsPDF('p', 'mm', [Math.max(297, Math.ceil(210 * hRatio)), 210]);

    pdf.setFillColor(251, 251, 251);
    pdf.rect(0, 0, 210, 297, 'F');

    const width = pdf.internal.pageSize.getWidth();
    const height = width * hRatio;

    pdf.addImage(imgData, 'JPEG', width * 0, 0, width * 1, height * 1);

    const pdfOutput = pdf.output('blob');
    const url = window.URL.createObjectURL(pdfOutput);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    if (clone.parentNode) document.body.removeChild(clone);
    originalZooms.forEach(({ element: el, zoom }) => {
      (el as HTMLElement).style.setProperty('zoom', zoom);
    });
    element.style.visibility = 'hidden';
    element.style.display = 'none';
    element.dataset.reportrendering = '0';
    throw error;
  }
}
